import React, { useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import Animated from 'react-native-reanimated';
import { StyleSheet, View } from 'react-native';
import { format, parseISO } from 'date-fns';
import { FlashList } from '@shopify/flash-list';
import Text from 'design-system/Text';
import Amount from 'design-system/Amount';
import Spacer from 'design-system/Spacer';
import ToolTip from 'design-system/ToolTip';
import { AppView } from 'design-system/AppView';
import Indicator from 'design-system/Indicator';
import { CardView } from 'design-system/CardView';
import { rem, SCREEN_BOTTOM_PADDING, SCROLL_INDICATOR_INSETS, } from 'design-system/values';
import withAnimatedNavigationHeader from 'design-system/hoc/withAnimatedNavigationHeader';
import BlurAmountView from 'design-system/BlurAmountView';
import { selectScrambled, selectAccountById, selectCurrentPaydayRangeWithFallback, selectCommittedTransactionsForAccount, selectBalancesForAccount, } from 'reducers/selectors';
import Amplitude from 'utils/amplitude';
import { formatAmount } from 'utils/formatting';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import useFocusEffect from 'hooks/useFocusEffect';
import { fetchCommittedForCurrentPaydayRange } from 'features/subscriptions/actions';
import useOpenSubscription from 'features/subscriptions/hooks/useOpenSubscription';
import { navigationRef } from 'utils/navigationv6';
import { IconSettings } from 'design-system/icons/IconSettings';
import Modal from 'utils/packages/Modal';
import { Button, CurrencyAmountText, SettingsItem } from 'design-system';
import { IconArrowBack, IconBoldCalendar } from 'design-system/icons';
import CommittedSpendingRow from 'features/subscriptions/components/CommittedSpendingRow';
import { addLastInSectionInfo } from 'utils/getTransactions';
import LogoBankAccount from 'design-system/LogoBankAccount';
import IconTrueBalance from 'design-system/icons/IconTrueBalance';
import NavigationHeaderRightIconButton from 'design-system/NavigationHeaderRightIconButton';
import { useMarginBottom } from 'hooks/useMarginBottom';
import PopUpMenuModal, { defaultPopUpWidth, } from 'design-system/PopUpMenuModal.web';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import ListEmptyView from 'design-system/ListEmptyView';
import { isWeb } from '../../../constants';
const TrueBalanceToolTip = () => (<ToolTip title="True balance" subtitle="This balance reflects your available funds after committed spending and predicted income. You can adjust this by reviewing your upcoming transactions or editing your period." tootTipBtnText="Okay" toolTipExtraBtnText="Edit period" small onExtraBtnPress={() => navigationRef.navigateDeprecated('SelectPeriod', {
        flow: 'SetAnalytics',
        keyBack: navigationRef.getCurrentRoute()?.key,
        saveAndGoToKeyBack: true,
    })}/>);
const keyExtractor = (item) => {
    if (item.type === 'header' || item.type === 'footer') {
        return item.title;
    }
    if (item.type === 'subscription') {
        return `${item.data.id.toString()}${item.data.prediction?.date}`;
    }
    return item.data.id.toString();
};
const typeExtractor = ({ type }) => type;
const AnimatedFlashList = Animated.createAnimatedComponent(FlashList);
const ModalToUse = isWeb ? PopUpMenuModal : Modal;
const TrueBalanceScreen = ({ navigation, route, data, onScroll, }) => {
    const dispatch = useAppDispatch();
    const scrambled = useAppSelector(selectScrambled);
    const guessedHomeCountry = useAppSelector((store) => store.user.user.guessedHomeCountry);
    const currentPaydayRange = useAppSelector(selectCurrentPaydayRangeWithFallback);
    const committedTransactions = useAppSelector((store) => selectCommittedTransactionsForAccount(store, data));
    useFocusEffect(useCallback(() => {
        dispatch(fetchCommittedForCurrentPaydayRange());
    }, []));
    const { balance, currency, trueBalance, upcomingBalance, upcomingIncome } = useAppSelector((store) => selectBalancesForAccount(store, data));
    const sections = useMemo(() => {
        const array = [];
        const flattenAndPush = (arrayToFlatten, type) => {
            let hasFilledFirstItem = false;
            arrayToFlatten.forEach(({ data }) => {
                data.forEach((subscription) => {
                    array.push({
                        type,
                        data: subscription,
                        isFirstInSection: !hasFilledFirstItem,
                    });
                    hasFilledFirstItem = true;
                });
            });
        };
        if (committedTransactions.arrayToBePaid.length) {
            array.push({
                type: 'header',
                title: 'Upcoming',
                total: upcomingIncome - upcomingBalance,
                currency,
            });
            flattenAndPush(committedTransactions.arrayToBePaid, 'subscription');
            addLastInSectionInfo(array);
        }
        if (committedTransactions.arrayPaid.length) {
            array.push({
                type: 'header',
                title: 'Already paid',
                total: committedTransactions.totalIncomeReceived -
                    committedTransactions.totalPaid,
                currency,
            });
            flattenAndPush(committedTransactions.arrayPaid, 'transaction');
            addLastInSectionInfo(array);
        }
        return array;
    }, [committedTransactions, upcomingBalance, upcomingIncome, currency]);
    const touchViewRef = useRef(null);
    useLayoutEffect(() => {
        const content = (<>
        {!isWeb && <Spacer.H8 />}
        <SettingsItem title="Manage recurring payments" icon={<IconArrowBack gradientKey="darkPink"/>} onPress={() => ModalToUse.hide(() => navigation.navigateDeprecated('Subscriptions'))}/>
        <SettingsItem title="Edit period" icon={<IconBoldCalendar gradientKey="darkPurple"/>} onPress={() => ModalToUse.hide(() => navigation.navigateDeprecated('SelectPeriod', {
                flow: 'SetAnalytics',
                keyBack: route.key,
                saveAndGoToKeyBack: true,
            }))}/>
      </>);
        navigation.setOptions({
            headerRight: () => (<View ref={touchViewRef} style={styles.menuButton}>
          <NavigationHeaderRightIconButton onPress={() => {
                    if (isWeb) {
                        if (touchViewRef) {
                            touchViewRef.current?.measure((_, __, ___, ____, fx, fy) => {
                                PopUpMenuModal.show(content, {
                                    fx: fx - defaultPopUpWidth,
                                    fy: fy + rem(40),
                                    containerStyle: styles.modal,
                                });
                            });
                        }
                    }
                    else {
                        Modal.show(content, {
                            title: 'True balance settings',
                            containerStyle: styles.modal,
                        });
                    }
                }}>
            <IconSettings colorKey="primary"/>
          </NavigationHeaderRightIconButton>
        </View>),
        });
    }, [route.key]);
    const openSubscription = useOpenSubscription();
    const currentPaydayTo = useMemo(() => format(parseISO(currentPaydayRange.to), "do 'of' MMMM"), [currentPaydayRange.to]);
    const onCompare = useCallback((routeWithParams) => {
        navigation.navigateDeprecated(routeWithParams);
        Amplitude.logEvent('Transactions.openCompare', {
            type: routeWithParams.name,
        });
    }, []);
    const renderItem = useCallback(({ item }) => (<CommittedSpendingRow item={item} onOpenCompare={onCompare} openSubscription={openSubscription} guessedHomeCountry={guessedHomeCountry} showExcludedTag={false}/>), [onCompare, openSubscription, guessedHomeCountry]);
    const ListHeaderComponent = useMemo(() => (<>
        <View style={styles.container}>
          <LogoBankAccount size="R64" data={data} onCard/>
          <Spacer.H8 />
          <CurrencyAmountText size="XL" value={{ amount: trueBalance, currency }}/>
          <Spacer.H8 />
          <View style={styles.subtitle}>
            <IconTrueBalance colorKey="secondary" width={20} height={20}/>
            <Spacer.W8 />
            <Text TextThin-16 Secondary>
              Balance on{' '}
              <Text Text-16 Secondary>
                {currentPaydayTo}
              </Text>
            </Text>
            <Spacer w={4}/>
            <TrueBalanceToolTip />
          </View>
        </View>
        <CardView withPadding noMarginBottom noMarginTop>
          <View style={styles.infoRow}>
            <Text Text-16>Current balance</Text>
            <BlurAmountView enabled={scrambled}>
              <Text Numbers-16>{formatAmount(balance, currency, 2)}</Text>
            </BlurAmountView>
          </View>
          <Spacer h={24}/>
          <View style={styles.infoRow}>
            <Text Text-16>Upcoming payment</Text>
            <Amount Numbers-16 showSign {...(upcomingIncome - upcomingBalance > 0 ? { Blue: true } : {})} value={{
            value: upcomingIncome - upcomingBalance,
            currency,
        }}/>
          </View>
        </CardView>
      </>), [
        data,
        trueBalance,
        currency,
        currentPaydayTo,
        scrambled,
        balance,
        upcomingBalance,
        upcomingIncome,
    ]);
    const ListEmptyComponent = useMemo(() => (<ListEmptyView style={styles.emptyContainer} title="No committed spending" subTitle="Your true balance is the same as your actual balance. To include any regular payments, add them as Recurring payments." happy compact onCard>
        <Button brandReversed small hug title="Manage recurring payments" onPress={() => navigation.navigate('Subscriptions')}/>
      </ListEmptyView>), []);
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const contentContainerStyle = useMarginBottom('paddingBottom', SCREEN_BOTTOM_PADDING, paddingHorizontalStyle);
    const baseProps = {
        data: sections,
        renderItem,
        onScroll,
        scrollEventThrottle: 16,
        showsVerticalScrollIndicator: true,
        scrollIndicatorInsets: SCROLL_INDICATOR_INSETS,
        contentContainerStyle,
        ListHeaderComponent,
        ListEmptyComponent,
        keyExtractor,
    };
    return (<AppView withFrame={false}>
      {isWeb ? (<Animated.FlatList {...baseProps}/>) : (<AnimatedFlashList {...baseProps} getItemType={typeExtractor}/>)}
    </AppView>);
};
const TrueBalanceScreenWrapper = withAnimatedNavigationHeader((props) => {
    const data = useAppSelector((store) => selectAccountById(store, props.route.params.id));
    if (!data) {
        return (<AppView>
          <View style={staticStyles.indicatorContainer}>
            <Indicator dark includeHeaderHeight/>
          </View>
        </AppView>);
    }
    return <TrueBalanceScreen {...props} data={data}/>;
}, ({ colors }) => ({
    headerColorOutputRange: [
        colors.background.dark,
        colors.cards.onDark,
    ],
    title: 'True balance',
}));
const staticStyles = StyleSheet.create({
    indicatorContainer: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
    },
});
const styles = StyleSheet.create({
    container: {
        paddingTop: rem(32),
        paddingBottom: rem(40),
        alignItems: 'center',
    },
    subtitle: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
    },
    infoRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
    },
    menuButton: {
        ...(!isWeb && { marginLeft: rem(16) }),
    },
    modal: {
        ...(isWeb
            ? {
                padding: 0,
            }
            : {
                paddingHorizontal: 0,
            }),
    },
    emptyContainer: {
        marginTop: rem(16),
    },
});
export default TrueBalanceScreenWrapper;
